import React, { useEffect, useState } from 'react';
import type { StockMovement, InventoryItem, InventoryLot } from '../../types/inventory';
import { inventoryService } from '../../services/inventoryService';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { Modal } from '../../components/common/Modal';
import { UnitToggle } from '../../components/common/UnitToggle';
import { WeightInput } from '../../components/common/WeightInput';
import { useWeightUnit } from '../../hooks/useWeightUnit';
import { workOrderService } from '../../services/workOrderService';
import { useLanguage } from '../../features/language/useLanguage';

interface InventoryHistoryDialogProps {
  itemId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
  lotId?: string;
  lotNumber?: string;
  currentStock?: number;
  itemType?: InventoryItem['itemType'] | null;
  roastedWorkOrderNumber?: string;
  roastedWorkOrderReferenceId?: string;
  finishedGoodName?: string;
  readOnly?: boolean;
}

export const InventoryHistoryDialog: React.FC<InventoryHistoryDialogProps> = ({
  itemId,
  isOpen,
  onClose,
  onUpdate,
  lotId,
  lotNumber,
  currentStock,
  itemType,
  roastedWorkOrderNumber,
  roastedWorkOrderReferenceId,
  finishedGoodName,
  readOnly = false,
}) => {
  const { t } = useLanguage();
  const [history, setHistory] = useState<StockMovement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adjustmentQuantityKg, setAdjustmentQuantityKg] = useState<number | undefined>(undefined);
  const [adjustmentType, setAdjustmentType] = useState<'add' | 'remove'>('add');
  const [adjustmentNotes, setAdjustmentNotes] = useState('');
  const [isAdjusting, setIsAdjusting] = useState(false);
  const { unit, setUnit } = useWeightUnit('kg');
  const [availableLots, setAvailableLots] = useState<InventoryLot[]>([]);
  const [selectedAdjustmentLotId, setSelectedAdjustmentLotId] = useState<string>('');
  const [resolvedRoastedWorkOrderNumber, setResolvedRoastedWorkOrderNumber] = useState<string>('');
  const isFinishedGoodMode = itemType === 'finished_good';
  const isLotBasedMode = itemType === 'green_bean';
  const isDirectItemMode = itemType === 'roasted_bulk' || itemType === 'finished_good';
  const adjustmentMode = isLotBasedMode ? 'lot_based' : isDirectItemMode ? 'direct_id' : null;

  useEffect(() => {
    if (isOpen && itemId) {
      if (!adjustmentMode) {
        setHistory([]);
        setAvailableLots([]);
        setError(t('inventory.history.errors.unsupportedItemType', { itemType: String(itemType) }));
        return;
      }
      void (async () => {
        try {
          setLoading(true);
          setError(null);
          const targetLotId = isLotBasedMode ? lotId : undefined;
          let response;
          if (targetLotId) {
            response = await inventoryService.getLotHistory(targetLotId, 1, 100);
          } else {
            response = await inventoryService.getHistory(itemId, 1, 100);
          }
          setHistory(response.data || []);
        } catch (err) {
          console.error('Failed to load history:', err);
          setError(t('inventory.history.errors.loadFailed'));
        } finally {
          setLoading(false);
        }
      })();
      setAdjustmentQuantityKg(undefined);
      setAdjustmentNotes('');
      setAdjustmentType('add');
      if (isLotBasedMode) {
        if (lotId) {
          setSelectedAdjustmentLotId(lotId);
        } else {
          setSelectedAdjustmentLotId('');
          void (async () => {
            try {
              const lots = await inventoryService.getLots(itemId);
              const activeLots = lots.filter(lot => lot.remainingQuantity > 0);
              setAvailableLots(activeLots);
            } catch (err) {
              console.error('Failed to load active lots:', err);
            }
          })();
        }
      } else {
        setSelectedAdjustmentLotId('');
        setAvailableLots([]);
      }
    } else {
      setHistory([]);
      setAvailableLots([]);
      setError(null);
    }
  }, [isOpen, itemId, lotId, adjustmentMode, isLotBasedMode, itemType, t]);

  useEffect(() => {
    const resolveRoastedWorkOrderNumber = async () => {
      if (!isOpen || itemType !== 'roasted_bulk') {
        setResolvedRoastedWorkOrderNumber('');
        return;
      }
      if (roastedWorkOrderNumber) {
        setResolvedRoastedWorkOrderNumber(roastedWorkOrderNumber);
        return;
      }
      const referenceId = roastedWorkOrderReferenceId?.trim();
      if (!referenceId) {
        setResolvedRoastedWorkOrderNumber('');
        return;
      }
      try {
        const order = await workOrderService.getById(referenceId);
        setResolvedRoastedWorkOrderNumber(order?.workOrderNumber || referenceId);
      } catch {
        setResolvedRoastedWorkOrderNumber(referenceId);
      }
    };
    resolveRoastedWorkOrderNumber();
  }, [isOpen, itemType, roastedWorkOrderNumber, roastedWorkOrderReferenceId]);

  const loadActiveLots = React.useCallback(async (itemId: string) => {
    try {
      const lots = await inventoryService.getLots(itemId);
      // Filter active lots (remaining quantity > 0)
      const activeLots = lots.filter(lot => lot.remainingQuantity > 0);
      setAvailableLots(activeLots);
    } catch (err) {
      console.error('Failed to load active lots:', err);
    }
  }, []);

  const loadHistory = React.useCallback(async (id: string, lotId?: string) => {
    try {
      setLoading(true);
      setError(null);
      // Currently fetching page 1, limit 100 for simplicity. Pagination can be added later.
      let response;
      if (lotId) {
        response = await inventoryService.getLotHistory(lotId, 1, 100);
      } else {
        response = await inventoryService.getHistory(id, 1, 100);
      }
      setHistory(response.data || []);
    } catch (err) {
      console.error('Failed to load history:', err);
      setError(t('inventory.history.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleAdjust = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!itemId) {
      setError(t('inventory.history.errors.itemIdRequired'));
      return;
    }
    if (!adjustmentMode) {
      setError(t('inventory.history.errors.unsupportedItemType', { itemType: String(itemType) }));
      return;
    }

    if (adjustmentMode === 'lot_based' && !selectedAdjustmentLotId) {
      setError(t('inventory.history.errors.selectLotRequired'));
      return;
    }

    if (typeof adjustmentQuantityKg !== 'number' || !Number.isFinite(adjustmentQuantityKg) || adjustmentQuantityKg <= 0) {
      setError(t('inventory.history.errors.validQuantityRequired'));
      return;
    }
    if (isFinishedGoodMode && !Number.isInteger(adjustmentQuantityKg)) {
      setError(t('inventory.history.errors.validQuantityRequired'));
      return;
    }
    if (exceedsAvailable) {
      setError(
        t('inventory.history.errors.exceedsAvailable', {
          available: formatWeight(availableForRemoval ?? 0),
          unit: unitLabel,
        })
      );
      return;
    }

    try {
      setIsAdjusting(true);
      const targetLotId = adjustmentMode === 'lot_based' ? selectedAdjustmentLotId : undefined;
      await inventoryService.adjustStock(itemId, adjustmentQuantityKg, adjustmentType, adjustmentNotes, targetLotId);
      setAdjustmentQuantityKg(undefined);
      setAdjustmentNotes('');
      if (adjustmentMode === 'lot_based' && !lotId) {
        loadActiveLots(itemId);
      }
      await loadHistory(itemId, isLotBasedMode ? lotId : undefined);
      if (onUpdate) {
        onUpdate();
      }
    } catch (err: unknown) {
      console.error('Failed to adjust stock:', err);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const msg = (err as any).response?.data?.error || t('inventory.history.errors.adjustFailed');
      setError(msg);
    } finally {
      setIsAdjusting(false);
    }
  };

  const availableForRemoval = React.useMemo<number | undefined>(() => {
    if (adjustmentMode === 'lot_based') {
      if (lotId) return currentStock;
      const lot = availableLots.find(l => l.id === selectedAdjustmentLotId);
      return lot?.remainingQuantity;
    }
    return currentStock;
  }, [adjustmentMode, lotId, currentStock, availableLots, selectedAdjustmentLotId]);

  const exceedsAvailable =
    adjustmentType === 'remove' &&
    typeof adjustmentQuantityKg === 'number' &&
    typeof availableForRemoval === 'number' &&
    adjustmentQuantityKg > availableForRemoval;

  if (!isOpen) return null;

  const formatWeight = (valKg: number) => {
    if (!Number.isFinite(valKg)) return '-';
    if (isFinishedGoodMode) {
      return Number.isInteger(valKg) ? String(valKg) : String(Number(valKg.toFixed(3)));
    }
    if (unit === 'kg') return valKg.toFixed(3);
    return String(Math.round(valKg * 1000));
  };

  const unitLabel = isFinishedGoodMode ? t('inventory.basicInfo.units.pcs') : unit === 'kg' ? 'kg' : 'g';
  const modalTitle = isLotBasedMode
    ? `${t('inventory.history.title')}${lotNumber ? ` ${t('inventory.history.forLot', { lotNumber })}` : ''}`
    : itemType === 'roasted_bulk'
      ? t('inventory.history.forRoasted', { workOrderNumber: resolvedRoastedWorkOrderNumber || t('settingsMcp.values.none') })
      : itemType === 'finished_good'
        ? t('inventory.history.forFinishedGood', { name: finishedGoodName || t('settingsMcp.values.none') })
        : t('inventory.history.title');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      maxWidth="6xl"
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          {itemType && (
            <span className="text-xs text-slate-500 uppercase tracking-wide">
              {t('inventory.history.itemType')}: {itemType.replace('_', ' ')}
            </span>
          )}
        </div>

        {typeof currentStock === 'number' && (
          <div className="text-sm font-normal text-gray-500 mb-4">
            {t('inventory.history.currentStock')}:{' '}
            <span className={currentStock > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
              {formatWeight(currentStock)} {unitLabel}
            </span>
          </div>
        )}
        
        {!readOnly && (
          <>
            {!adjustmentMode && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {t('inventory.history.cannotAdjustInvalidType')}
              </div>
            )}
            <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
              <div className="flex items-center gap-2 mb-6">
                <div className="h-5 w-1 bg-primary-600 rounded-full"></div>
                <h4 className="text-base font-semibold text-slate-800">{t('inventory.history.manualStockAdjustment')}</h4>
              </div>

              <form onSubmit={handleAdjust} className="space-y-5">
                {adjustmentMode === 'lot_based' && !lotId && (
                  <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                    <label htmlFor="lotSelect" className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                      {t('inventory.history.selectActiveLot')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="lotSelect"
                      value={selectedAdjustmentLotId}
                      onChange={(e) => setSelectedAdjustmentLotId(e.target.value)}
                      className="block w-full rounded-lg border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm transition-colors focus:border-primary-500 focus:ring-primary-500 hover:bg-white"
                      required
                    >
                      <option value="">{t('inventory.history.selectLotOption')}</option>
                      {availableLots.map(lot => (
                        <option key={lot.id} value={lot.id}>
                          {lot.lotNumber} (Qty: {formatWeight(lot.remainingQuantity)} {unitLabel})
                        </option>
                      ))}
                    </select>
                    {availableLots.length === 0 && (
                      <p className="mt-2 text-xs flex items-center text-red-600">
                        {t('inventory.history.noActiveLots')}
                      </p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-end">
                  <div className="md:col-span-3">
                    <label htmlFor="type" className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                      {t('inventory.history.action')}
                    </label>
                    <select
                      id="type"
                      value={adjustmentType}
                      onChange={(e) => setAdjustmentType(e.target.value as 'add' | 'remove')}
                      className="block w-full rounded-lg border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm transition-all focus:border-primary-500 focus:ring-primary-500"
                      data-test-id="inventory-adjustment-action"
                    >
                      <option value="add">{t('inventory.history.actionAdd')}</option>
                      <option value="remove">{t('inventory.history.actionRemove')}</option>
                    </select>
                  </div>

                  <div className="md:col-span-3">
                    <div className="flex items-center justify-between mb-2">
                      <label htmlFor="quantity" className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                        {t('inventory.history.quantity')}
                      </label>
                      {!isFinishedGoodMode && <UnitToggle unit={unit} onToggle={setUnit} className="shrink-0" />}
                    </div>
                    {isFinishedGoodMode ? (
                      <div className="relative w-full">
                        <input
                          id="quantity"
                          type="number"
                          min={1}
                          step={1}
                          required
                          value={typeof adjustmentQuantityKg === 'number' ? adjustmentQuantityKg : ''}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            if (nextValue === '') {
                              setAdjustmentQuantityKg(undefined);
                              return;
                            }
                            const parsed = Number(nextValue);
                            setAdjustmentQuantityKg(Number.isFinite(parsed) ? parsed : undefined);
                          }}
                          className="block w-full rounded-lg border-slate-200 bg-slate-50/50 px-4 py-2.5 pr-12 text-sm transition-all focus:border-primary-500 focus:ring-primary-500 placeholder:text-slate-400"
                          placeholder="0"
                          data-test-id="inventory-adjustment-quantity"
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 select-none text-xs text-gray-400">
                          {t('inventory.basicInfo.units.pcs')}
                        </span>
                      </div>
                    ) : (
                      <WeightInput
                        id="quantity"
                        required
                        value={adjustmentQuantityKg}
                        onChange={setAdjustmentQuantityKg}
                        unit={unit}
                        className="block w-full rounded-lg border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm transition-all focus:border-primary-500 focus:ring-primary-500 placeholder:text-slate-400"
                        placeholder={unit === 'kg' ? t('inventory.history.placeholderKg') : t('inventory.history.placeholderGram')}
                        data-test-id="inventory-adjustment-quantity"
                      />
                    )}
                    {exceedsAvailable && (
                      <p className="mt-1.5 text-xs text-red-600" data-test-id="inventory-adjustment-exceeds">
                        {t('inventory.history.errors.exceedsAvailable', {
                          available: formatWeight(availableForRemoval ?? 0),
                          unit: unitLabel,
                        })}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-4">
                    <label htmlFor="notes" className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                      {t('inventory.history.reasonNotes')}
                    </label>
                    <input
                      type="text"
                      id="notes"
                      value={adjustmentNotes}
                      onChange={(e) => setAdjustmentNotes(e.target.value)}
                      className="block w-full rounded-lg border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm transition-all focus:border-primary-500 focus:ring-primary-500 placeholder:text-slate-400"
                      placeholder={t('inventory.history.reasonPlaceholder')}
                      data-test-id="inventory-adjustment-notes"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <button
                      type="submit"
                      disabled={
                        isAdjusting ||
                        !adjustmentQuantityKg ||
                        !itemId ||
                        !adjustmentMode ||
                        (adjustmentMode === 'lot_based' && !selectedAdjustmentLotId) ||
                        exceedsAvailable
                      }
                      className="w-full inline-flex items-center justify-center rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-primary-200 transition-all hover:bg-primary-700 hover:shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none disabled:shadow-none"
                      data-test-id="inventory-adjustment-submit"
                    >
                      {isAdjusting ? (
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        t('inventory.history.updateStock')
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </>
        )}

        <div className="mt-4">
          {loading ? (
            <LoadingSpinner text={t('forms.common.loadingHistory')} size="sm" />
          ) : error ? (
            <div className="text-red-600 text-sm py-2">{error}</div>
          ) : history.length === 0 ? (
            <div className="text-gray-500 text-sm py-4">{t('inventory.history.empty')}</div>
          ) : (
            <div className="overflow-x-auto max-h-[60vh]">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.general.table.date')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.shift.type')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('inventory.history.lotNumber')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('inventory.history.quantityUnit', { unit: unitLabel })}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('inventory.history.warehouse')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('inventory.history.user')}</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.details.notes')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {history.map((item) => { 
                     const isDraftReservationRelease =
                      item.referenceType === 'fg_production_draft' &&
                      typeof item.notes === 'string' &&
                      item.notes.toLowerCase().includes('release reservation');

                     const effectiveQty =
                      item.movementType === 'issue'
                        ? -Math.abs(item.quantity)
                        : isDraftReservationRelease
                          ? Math.abs(item.quantity)
                          : item.quantity;

                    const isPositive = effectiveQty >= 0;
                    return (
                    <tr key={item.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(item.createdAt).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                        {item.movementType}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.lotNumber || t('settingsMcp.values.none')}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium
                          ${isPositive ? 'text-green-600' : 'text-red-600'}
                        `}
                      >
                        {isPositive ? '+' : '-'}
                        {formatWeight(Math.abs(effectiveQty))} {unitLabel}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.warehouseName || t('settingsMcp.values.none')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.userName || t('settingsMcp.values.none')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-xs truncate" title={item.notes}>
                        {item.notes || t('settingsMcp.values.none')}
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-5 sm:mt-6 flex justify-end">
        <button
          type="button"
          className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
          onClick={onClose}
        >
          {t('forms.actions.close')}
        </button>
      </div>
    </Modal>
  );
};
